import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import './MyAccountModal.css';

interface SectionProps {
  onBack: () => void;
}

const ProfileSettings = ({ onBack }: SectionProps) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [emailChanged, setEmailChanged] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDoneButton, setShowDoneButton] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) return;

      const { data } = await supabase
        .from('users')
        .select('name, email')
        .eq('auth_id', user.id)
        .single();

      if (data) {
        setFullName(data.name || '');
        setEmail(data.email || user.email || '');
        setOriginalEmail(data.email || user.email || '');
      }
    };
    loadUserData();
  }, []);

const handleSave = async () => {
  setIsSaving(true);
  setMessage('');
  setShowDoneButton(false);

  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user) throw new Error('User not found');

    // 🧠 Compare entered email to the *current Auth email* to ensure detection always works
    const currentAuthEmail = user.email || '';
    const emailHasChanged = email.trim().toLowerCase() !== currentAuthEmail.trim().toLowerCase();

    if (!emailHasChanged) {
      // ✅ Name only changed
      await supabase.from('users').update({ name: fullName }).eq('auth_id', user.id);
      setMessage('✅ Name updated successfully.');
      setShowDoneButton(true);
    } else {
      // ⚠️ Email actually changed → show confirmation modal
      setEmailChanged(true);
      setShowEmailModal(true);
    }
  } catch (err: any) {
    console.error('Profile update error:', err);
    setMessage('⚠️ Error updating profile. Please try again.');
  } finally {
    setIsSaving(false);
  }
};

  const handleContinueEmailChange = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) throw new Error('User not found');

      // Save name and pending email
      await supabase
        .from('users')
        .update({ name: fullName, pending_email: email })
        .eq('auth_id', user.id);

      // Trigger verification email
      await supabase.auth.updateUser({ email });

      setMessage('📧 Verification email sent. Please check your inbox.');
      setShowEmailModal(false);
      setShowDoneButton(true);
    } catch (err: any) {
      console.error('Email verification error:', err);
      setMessage('⚠️ Error sending verification email. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEmailChange = () => {
    // Reset to original email if cancelled
    setEmail(originalEmail);
    setShowEmailModal(false);
  };

  return (
    <div className="my-account-section-content">
      <p className="profile-description">
        Certification data (Certifier ID, company name, contact info) appear on your certificates
        and in your CPSC message sets. To manage those details, visit the
        <strong> Certification Management</strong> page.
      </p>

      <div className="profile-form">
        <label className="profile-label">Full Name:</label>
        <input
          type="text"
          className="profile-input"
          value={String(fullName)}
          onChange={(e) => setFullName(e.target.value)}
        />

        <label className="profile-label">Email Address:</label>
        <input
          type="email"
          className="profile-input"
          value={String(email)}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {message && <p className="profile-message">{message}</p>}

      <div className="form-actions">
        {showDoneButton ? (
          <button className="save-btn" onClick={onBack}>
            Done
          </button>
        ) : (
          <>
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button className="cancel-btn" onClick={onBack}>
              Cancel
            </button>
          </>
        )}
      </div>

      {/* ⚠️ Email Change Confirmation Modal */}
      {showEmailModal && (
        <div className="edit-confirm-overlay">
          <div className="edit-confirm-modal">
            <p>
              You’re about to change your login email from <strong>{originalEmail}</strong> to{' '}
              <strong>{email}</strong>.
            </p>
            <p>
              A verification link will be sent to the new address.
            </p>
            <div className="confirm-actions">
              <button
                className="confirm-btn"
                onClick={handleContinueEmailChange}
                disabled={isSaving}
              >
                {isSaving ? 'Processing...' : 'Continue'}
              </button>
              <button className="cancel-btn" onClick={handleCancelEmailChange}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
